import React from 'react';
import { MenuItem, TextField, Typography } from '@material-ui/core';
import clsx from 'clsx';
import useStyles from 'app/cluster-styles/useStyles';
import SectionCard from './SectionCard';

/**
 * Effective boot-disk range. `imageConstraint` (optional, IBM today) narrows the
 * provider-wide range to what the selected image and the cloud actually allow:
 * a boot disk can never be smaller than the image it boots from.
 */
export function effectiveVolLimits(volSizeLimits, imageConstraint) {
    return {
        min: Math.max(volSizeLimits.min, (imageConstraint && imageConstraint.minGb) || 0),
        max: (imageConstraint && imageConstraint.maxGb) || volSizeLimits.max,
    };
}

function getVolumeError(value, volSizeLimits, imageConstraint) {
    if (!value && value !== 0) return '';
    if (value === '') return '';
    if (/[a-zA-Z!@#$%^&*()_+\-=[\]{};':"\\|/,.|~<>?\s]/.test(value)) return 'Must be a valid number.';
    const num = parseInt(value, 10);
    if (isNaN(num)) return 'Must be a valid number.';
    const limits = effectiveVolLimits(volSizeLimits, imageConstraint);
    // Attribute an image-driven floor to the image, so "why can't I use 100?" is
    // answered in the field itself rather than looking like an arbitrary limit.
    if (imageConstraint && imageConstraint.minGb && num < imageConstraint.minGb) {
        const from = imageConstraint.imageName ? ` — set by image ${imageConstraint.imageName}` : '';
        return `Must be at least ${imageConstraint.minGb} GB${from}`;
    }
    if (num < limits.min || num > limits.max) {
        return `Must be a number between ${limits.min} and ${limits.max}`;
    }
    return '';
}

export default function StorageSettingsSection({ form, setField, provider, volSizeLimits, shareOptions, imageConstraint, volSizeNote }) {
    const classes = useStyles();

    const serverVolumeSize = form.serverVolumeSize || '';
    const nodeVolumeSize = form.nodeVolumeSize || '';
    const share = form.share || 'nfs';
    const idlePurgeS = form.idlePurgeS ?? '';

    const limits = effectiveVolLimits(volSizeLimits, imageConstraint);
    const serverVolumeError = getVolumeError(serverVolumeSize, volSizeLimits, imageConstraint);
    const nodeVolumeError = getVolumeError(nodeVolumeSize, volSizeLimits, imageConstraint);

    return (
        <SectionCard icon="storage" title="Storage Settings">
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', alignItems: 'flex-start' }}>
                <TextField
                    className={clsx(classes.input)}
                    style={{ flex: 1 }}
                    id={`${provider}_server-volume_size`}
                    name="volume_size"
                    label="Server Volume (GB)"
                    type="text"
                    variant="outlined"
                    value={serverVolumeSize}
                    onChange={(e) => setField('serverVolumeSize', e.target.value)}
                    placeholder={`Disk size of the Cluster Head Node (Min: ${limits.min}, Max: ${limits.max})`}
                    error={Boolean(serverVolumeError)}
                    helperText={serverVolumeError}
                />
                <TextField
                    className={clsx(classes.input)}
                    style={{ flex: 1 }}
                    id={`${provider}_node-volume_size`}
                    name="node_volume_size"
                    label="Node Volume (GB)"
                    type="text"
                    variant="outlined"
                    value={nodeVolumeSize}
                    onChange={(e) => setField('nodeVolumeSize', e.target.value)}
                    placeholder={`Disk size of the Cluster Nodes (Min: ${limits.min}, Max: ${limits.max})`}
                    error={Boolean(nodeVolumeError)}
                    helperText={nodeVolumeError}
                />
                {shareOptions && (
                    <TextField
                        select
                        className={clsx(classes.input)}
                        style={{ flex: 1 }}
                        id={`${provider}_share`}
                        name="share"
                        label="Shared File System"
                        variant="outlined"
                        value={share}
                        onChange={(e) => setField('share', e.target.value)}
                        SelectProps={{
                            MenuProps: { PaperProps: { style: { maxHeight: 300 } } },
                        }}
                    >
                        {shareOptions.map((opt) => (
                            <MenuItem key={opt.value} id={`${provider}_${opt.value}`} value={opt.value}>
                                {opt.label}
                            </MenuItem>
                        ))}
                    </TextField>
                )}
            </div>
            {volSizeNote && (
                <Typography variant="caption" color="textSecondary" style={{ display: 'block', marginTop: '-8px', marginBottom: '16px' }}>
                    {volSizeNote}
                </Typography>
            )}
            <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <TextField
                    className={clsx(classes.input)}
                    style={{ flex: 1 }}
                    id={`${provider}_idle_purge_s`}
                    name="idle_purge_s"
                    label="Idle Purge Time (S)"
                    type="number"
                    variant="outlined"
                    value={idlePurgeS}
                    onChange={(e) => setField('idlePurgeS', e.target.value)}
                    inputProps={{ min: 0 }}
                />
            </div>
        </SectionCard>
    );
}
